import {
  Box,
  Button,
  Container,
  Copy,
  Flex,
  Heading,
  Image,
  Link,
  Loader,
  Stack,
  SvgMagnifyingGlass,
  SvgPeople,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grubhubprod/gh-cookbook-react';
import { Square } from '@grubhubprod/gh-cookbook-react/dist';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import {
  TealiumEvent,
  useAnalytics,
} from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { EnterpriseMealCreditListCard } from 'src/components/oneCorp/features/MealCredit/components/MealCreditList/EnterpriseMealCreditListCard/EnterpriseMealCreditListCard';
import { Input } from 'src/components/Input/Input';
import { Pagination } from 'src/components/Pagination/Pagination';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { pushError } from 'src/utilities/notifications';
import {
  ISearchParams,
  LineOfCreditSearchType,
  SearchStatus,
  useDinerSearch,
  useLineOfCreditSearch,
} from 'src/utilities/useSearchHooks';
import { getWindow } from 'src/utilities/window';
import { buildCAUIPath, CAUIPage } from 'src/constants/urls';

enum MealCreditsPageState {
  INITIAL_LOAD,
  NO_EMPLOYEES,
  LOC_LIST,
}

enum MealCreditTabIndex {
  ACTIVE,
  INACTIVE,
}

const MEAL_CREDIT_SEARCH_MAP = {
  [MealCreditTabIndex.ACTIVE]: LineOfCreditSearchType.ACTIVE,
  [MealCreditTabIndex.INACTIVE]: LineOfCreditSearchType.INACTIVE,
};

const MEAL_CREDIT_PAGE_SIZE = 5;
interface SearchFormFields {
  searchQuery: string;
}

export default function EnterpriseMealCreditList(): JSX.Element {
  const { corpId } = useAuth();
  const [locSearchType, setLocSearchType] = useState(
    LineOfCreditSearchType.ACTIVE
  );
  const currentSearchQuery = useRef(null);
  const isInitialPageViewSent = useRef(false);
  const pendingTealiumEvents = useRef<TealiumEvent[]>([]);
  const [canAddNewMealCredit, setCanAddNewMealCredit] = useState(false);
  const [pageState, setPageState] = useState(MealCreditsPageState.INITIAL_LOAD);
  const [hasActiveMealCredits, setHasActiveMealCredits] = useState(false);
  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();
  const window = getWindow();

  const {
    register: searchFormRegister,
    handleSubmit: handleSearchFormSubmit,
    setValue: setSearchFormValue,
  } = useForm<SearchFormFields>();

  const {
    doSearch: doEmployeeSearch,
    totalResults: totalEmployees,
    searchStatus: employeeSearchStatus,
  } = useDinerSearch(corpId);

  const {
    doSearch: doLineOfCreditSearch,
    totalResults: totalLinesOfCredit,
    totalPages,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    currentSearchPage,
    hasNoResultsWithQuery,
    hasNoResultsWithoutQuery,
    searchStatus: lineOfCreditSearchStatus,
    resultsList: linesOfCredit,
  } = useLineOfCreditSearch(corpId, MEAL_CREDIT_PAGE_SIZE);

  useEffect(() => {
    if (corpId) {
      doLineOfCreditSearch({
        filter_by: locSearchType,
        page: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId, locSearchType]);

  useEffect(() => {
    if (corpId) {
      doEmployeeSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    const locSearchTypeLabel =
      locSearchType === LineOfCreditSearchType.ACTIVE ? 'active' : 'inactive';

    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup: `meal credit settings_${locSearchTypeLabel}`,
    });

    if (!isInitialPageViewSent.current) {
      isInitialPageViewSent.current = true;
      sendPendingTealiumEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locSearchType]);

  useEffect(() => {
    const searchStatusArray = [lineOfCreditSearchStatus, employeeSearchStatus];

    if (areSearchesComplete(searchStatusArray)) {
      const hasActiveLinesOfCredit = totalLinesOfCredit > 0;
      const hasEmployees = totalEmployees > 0;

      setCanAddNewMealCredit(hasEmployees);

      const defaultEventData = {
        eventcategory: 'meal credit settings',
        eventaction: 'meal credit state',
      };

      if (
        hasActiveLinesOfCredit ||
        locSearchType === LineOfCreditSearchType.INACTIVE
      ) {
        setPageState(MealCreditsPageState.LOC_LIST);
        setHasActiveMealCredits(true);
      } else if (!hasEmployees) {
        sendMealCreditStateTealiumEvent({
          ...defaultEventData,
          eventlabel: 'no employees available',
        });
        setPageState(MealCreditsPageState.NO_EMPLOYEES);
      } else {
        sendMealCreditStateTealiumEvent({
          ...defaultEventData,
          eventlabel: 'no LOC available',
        });
        setPageState(MealCreditsPageState.LOC_LIST);
      }
    } else if (hasSearchError(searchStatusArray)) {
      pushError('Something went wrong. Refresh the page to try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lineOfCreditSearchStatus,
    employeeSearchStatus,
    pageState,
    totalLinesOfCredit,
    totalEmployees,
  ]);

  function sendPendingTealiumEvents(): void {
    const hasPendingEvents = pendingTealiumEvents.current.length > 0;
    if (hasPendingEvents) {
      pendingTealiumEvents.current.forEach((event) => sendTealiumEvent(event));
      pendingTealiumEvents.current = [];
    }
  }

  // the tealium event for meal credit state should not be sent before the first page view event
  function sendMealCreditStateTealiumEvent(eventData: TealiumEvent): void {
    if (pageState !== MealCreditsPageState.INITIAL_LOAD) {
      return;
    }

    if (isInitialPageViewSent.current) {
      sendTealiumEvent(eventData);
    } else {
      pendingTealiumEvents.current.push(eventData);
    }
  }

  function areSearchesComplete(
    searchStatusArray: SearchStatus[] = []
  ): boolean {
    return searchStatusArray.every(
      (status) => status === SearchStatus.COMPLETE
    );
  }

  function hasSearchError(searchStatusArray: SearchStatus[] = []): boolean {
    return searchStatusArray.some((status) => status === SearchStatus.ERROR);
  }

  function updateLineOfCreditSearch({ searchQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = {
      filter_by: locSearchType,
    };

    if (searchQuery) {
      searchParams.query = searchQuery;
      currentSearchQuery.current = searchQuery;
    } else {
      currentSearchQuery.current = null;
    }

    doLineOfCreditSearch(searchParams);
  }

  function updateActiveTab(index: MealCreditTabIndex): void {
    // clear out search query when switching tabs
    setSearchFormValue('searchQuery', '');
    currentSearchQuery.current = '';

    setLocSearchType(MEAL_CREDIT_SEARCH_MAP[index]);
  }

  function sendAnalyticsBeforeNavigation(
    event: MouseEvent<HTMLAnchorElement>,
    analyticsData: { eventcategory: string; eventaction: string }
  ): void {
    event.preventDefault();

    const target = event.target as HTMLAnchorElement;

    sendTealiumEvent(analyticsData);

    window.location.href = target.href;
  }

  return (
    <Tabs onChange={updateActiveTab}>
      <Stack y="md" data-testid="meal-credit-list-header">
        <Box as="header" backgroundColor="untintedBackground">
          <Container variant="lg">
            <Stack y="lg" paddingTop="12">
              <Heading variant="h1">Meal credits</Heading>
            </Stack>
            <Flex justifyContent="space-between">
              <TabList>
                <Tab as="button" index={MealCreditTabIndex.ACTIVE}>
                  Active
                </Tab>
                <Tab as="button" index={MealCreditTabIndex.INACTIVE}>
                  Inactive
                </Tab>
              </TabList>
              <Stack y="x-sm">
                <Tooltip
                  disabled={canAddNewMealCredit}
                  maxWidth="200px"
                  roundedContainer
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      To create a meal credit, add employees to the account
                    </Copy>
                  }
                >
                  <div>
                    <RouterButtonLink
                      href={`/corporate/enterprise/${corpId}/meal-credits/create`}
                      buttonVariant="primary"
                      buttonSize="medium"
                      useBlockButton
                      preNavigationFunction={() => {
                        sendTealiumEvent({
                          eventcategory: 'meal credit settings',
                          eventaction: 'add new meal credit_initiate_cta',
                          eventlabel: hasActiveMealCredits
                            ? 'active meal credits available'
                            : 'no active meal credits',
                        });
                      }}
                    >
                      Add meal credit
                    </RouterButtonLink>
                  </div>
                </Tooltip>
              </Stack>
            </Flex>
          </Container>
        </Box>
      </Stack>
      <Container variant="lg" data-testid="meal-credit-list-content">
        {pageState === MealCreditsPageState.INITIAL_LOAD ? (
          <PageLoader />
        ) : (
          <>
            {pageState === MealCreditsPageState.LOC_LIST && (
              <TabPanels>
                <Stack y="sm">
                  <Flex alignItems="flex-end" justifyContent="space-between">
                    <Flex alignItems="center" as="form">
                      <Stack x="sm">
                        <Input
                          name="searchQuery"
                          id="searchQuery"
                          ariaLabel="Search by credit name"
                          placeholder="Search by credit name"
                          prefixIcon={
                            <Box color="secondaryText">
                              <SvgMagnifyingGlass size="20px" />
                            </Box>
                          }
                          ref={searchFormRegister}
                        />
                      </Stack>
                      <Button
                        variant="secondary"
                        buttonSize="medium"
                        onClick={handleSearchFormSubmit(
                          updateLineOfCreditSearch
                        )}
                      >
                        Search
                      </Button>
                    </Flex>
                    <Copy variant="caption" color="secondaryText">
                      {firstItemNumberFromPage} - {lastItemNumberFromPage} of{' '}
                      {totalLinesOfCredit}
                    </Copy>
                  </Flex>
                </Stack>
                {lineOfCreditSearchStatus === SearchStatus.LOADING && (
                  <PageLoader />
                )}
                {lineOfCreditSearchStatus === SearchStatus.COMPLETE && (
                  <>
                    {/* Active meal credits */}
                    <TabPanel>
                      {hasNoResultsWithoutQuery && (
                        <EmptyMessageContainer
                          headingSize="h6"
                          imageContent={
                            <Image
                              width="106px"
                              src="https://res.cloudinary.com/grubhub-assets/image/upload/v1609861160/corp-management-ui/Line_of_Credit_Empty_State_Illo.svg"
                              role="presentation"
                              alt=""
                            />
                          }
                          messageContent="You don't have any active meal credits"
                        />
                      )}
                      {hasNoResultsWithQuery && (
                        <EmptyMessageContainer
                          headingSize="h6"
                          messageContent={`No results found for '${currentSearchQuery.current}'`}
                        />
                      )}
                      {linesOfCredit.map((lineOfCredit) => (
                        <Stack key={lineOfCredit.id} y="sm">
                          <EnterpriseMealCreditListCard
                            mealCredit={lineOfCredit}
                          />
                        </Stack>
                      ))}
                    </TabPanel>
                    {/* Inactive meal credits */}
                    <TabPanel>
                      {hasNoResultsWithoutQuery && (
                        <EmptyMessageContainer
                          headingSize="h6"
                          messageContent="You don't have any inactive meal credits"
                        />
                      )}
                      {hasNoResultsWithQuery && (
                        <EmptyMessageContainer
                          headingSize="h6"
                          messageContent={`No results found for '${currentSearchQuery.current}'`}
                        />
                      )}
                      {linesOfCredit.map((lineOfCredit) => (
                        <Stack key={lineOfCredit.id} y="sm">
                          <EnterpriseMealCreditListCard
                            mealCredit={lineOfCredit}
                            isInactive
                          />
                        </Stack>
                      ))}
                    </TabPanel>
                    {totalPages > 1 && (
                      <Flex justifyContent="center" paddingBottom="4">
                        <Pagination
                          boundaryLinks
                          page={currentSearchPage}
                          pageSize={MEAL_CREDIT_PAGE_SIZE}
                          maxSize={8}
                          ellipses={false}
                          rotate
                          collectionSize={totalLinesOfCredit}
                          pageChange={(page) => {
                            const searchParams: ISearchParams = {
                              page,
                              filter_by: locSearchType,
                            };

                            if (currentSearchQuery.current) {
                              searchParams.query = currentSearchQuery.current;
                            }

                            doLineOfCreditSearch(searchParams);
                          }}
                        />
                      </Flex>
                    )}
                  </>
                )}
              </TabPanels>
            )}
            {pageState === MealCreditsPageState.NO_EMPLOYEES && (
              <EmptyMessageContainer
                imageContent={
                  <Box color="inactiveIcon">
                    <SvgPeople size="40px" />
                  </Box>
                }
                messageContent={
                  <>
                    <Link
                      href={buildCAUIPath(CAUIPage.EMPLOYEES, { corpId })}
                      onClick={(e) =>
                        sendAnalyticsBeforeNavigation(e, {
                          eventcategory: 'meal credit settings',
                          eventaction: 'add employees_cta',
                        })
                      }
                    >
                      Add employees
                    </Link>{' '}
                    to the account before creating a meal credit
                  </>
                }
              />
            )}
          </>
        )}
      </Container>
    </Tabs>
  );
}

interface IEmptyMessageContainerProps {
  imageContent?: JSX.Element;
  messageContent: JSX.Element | string;
  headingSize?: 'h4' | 'h6';
}

function EmptyMessageContainer({
  imageContent,
  messageContent,
  headingSize = 'h4',
}: IEmptyMessageContainerProps): JSX.Element {
  return (
    <Square inset="md" backgroundColor="untintedBackground">
      <Flex alignItems="center" justifyContent="center" flexDirection="column">
        {imageContent && <Stack y="md">{imageContent}</Stack>}
        <Heading variant={headingSize} color="secondaryText">
          {messageContent}
        </Heading>
      </Flex>
    </Square>
  );
}

function PageLoader(): JSX.Element {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="50vh"
    >
      <Loader size="md" />
    </Flex>
  );
}
